import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TherapistTypeFilter, TherapistRow,
  CAPACITY_TARGETS, getCapacitySummary,
} from './mockData';
import { execApi } from '../../../services/api';
import { T, SERIES } from '../../../theme/tokens';
import './styles.css';

// Colours written inline in this file now come from the shared palette instead of
// being typed again here. SERIES[2] is the violet of the chart palette — there is
// no semantic "purple" token, and purple on this screen means one thing only:
// "Clalit-only, cannot absorb Maccabi demand".

type SortKey = 'name' | 'given' | 'booked' | 'done' | 'active' | 'noshow'
  | 'blocked' | 'left' | 'emptypast' | 'free' | 'util' | 'future' | 'empty' | 'caseload' | 'fwd';

// ניצולת = every meeting in the calendar ÷ what was opened. Past, missed and
// future alike — if an hour has a meeting on it, it is used (Ohad, 2026-10-04:
// "אם פתחתי 100 שעות ויש לי 80 שעות פגישות אקטיב שהיו או מוזמנות אני ב-80%").
//
// This is one number in every month, which is the point. It briefly was two:
// delivered-only, with a separate "תפוסה" reading for months that had not started.
// That split made October read 3.85% — technically true, since almost nothing had
// been performed yet, and useless for a capacity screen whose whole job is to show
// whether a therapist's time is spoken for.
//
// What it does NOT count is the part the old formula got wrong: (נפתח − פנוי) ÷ נפתח
// called every non-bookable hour "used", so September read 98.18% on 560.83 hours
// that were merely blocked or unmeasured. Blocked time is not work. It is simply
// absent from both sides of the question this number asks.
const rowUtil = (t: TherapistRow): number => {
  const g = t.hoursGiven;
// NOT clamped at 1 (Ohad, 2026-10-04). It used to be, and the clamp was hiding a
// real finding: 13 of 85 therapists in October hold MORE meeting hours than they
// opened — 54.50 hours of them — and every one of those read a tidy 100%. Uriel
// Elimelech opened 16.00 and carries 29.25, which is 182.81%, not "full".
// The share is growing too: 8.6% of therapists in September, 15.3% in October.
// Whether a meeting outside opened hours is a fault or a practice is a question for
// Rana, and the screen's job until she answers is to make it impossible to miss.
  return g > 0 ? Math.max(0, t.hoursBooked / g) : 0;
};

// Three columns, three MEASURED fields, no arithmetic between them (Ohad, 2026-09-28):
// נפתח = given, נוצל = booked, פנוי = the bookable-slots field itself.
//
// "פנוי" used to be given − booked, which counts a blocked hour as free. That read
// 31.67 free hours for Amir Eyal in October against a calendar with nothing bookable
// in it — he and Maoz Rosenfeld are leaving and have blocked themselves out, and the
// screen was offering their hours as capacity. The bookable field said 0 all along.
//
// The three no longer sum to the opened total, and that is deliberate: October leaves
// 564.67 of 1,935.17 opened hours in neither column. Part is real blocking, part is
// therapists the availability field never measured, and NOTHING in the warehouse can
// separate the two — Sanallah reads 0 bookable every month while patients are booked
// into him. A fourth column would have to invent that split. Better an honest gap than
// a fabricated number; ask Shachar for a blocked_minutes field to close it properly.
const rowFree = (t: TherapistRow): number => t.availableHours ?? 0;

// No "חסום" column. It is derivable as opened − booked − bookable, but only where
// the bookable field carries a measurement: in October 2026, 241.17 of the 409.17
// unexplained hours are answerable that way and 168.00 are not. A bookable reading
// of exactly 0.00 means either a calendar sealed shut (Amir Eyal, Maoz Rosenfeld —
// both leaving) or a field that never measured this therapist (patients were booked
// into Sanallah, Maha Ibrahim and Shaymaa Darawshy in the last week of September
// while it read zero for all three). Same zero, opposite meanings. Booking recency
// separates them, but that goes to Shachar for verification before it drives a
// number on this screen — a column that is right 59% of the time is worse than no
// column, because nobody can tell which 59% they are looking at (Ohad, 2026-09-28).

type SortDir = 'asc' | 'desc';

const ROWS_PER_PAGE = 5;

const TYPES: { id: TherapistTypeFilter; label: string }[] = [
  { id: 'all', label: 'הכל' },
  { id: 'psy', label: 'פסיכיאטרים' },
  { id: 'cm',  label: 'מנהלי תיק' },
];

// Real calendar months available in dash_views.therapist_load_data.
// Current month (dynamic) — the drill defaults here and marks it "(נוכחי)".
const _CUR_YEAR = new Date().getFullYear();
const CUR_MONTH = `${_CUR_YEAR}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
const _MON_HE = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const MONTHS: { id: string; label: string }[] = _MON_HE.map((he, i) => {
  const id = `${_CUR_YEAR}-${String(i + 1).padStart(2, '0')}`;
  return { id, label: `${he} ${_CUR_YEAR}${id === CUR_MONTH ? ' (נוכחי)' : ''}` };
});

// ---------- API response shape (mirrors backend exec_staff.py) ----------
interface TherapistLoadResp {
  therapist_id: number;
  name: string;
  type: string;           // 'psy' | 'cm'
  done_hours: number;
  active_hours: number;
  no_show_hours: number;
  booked_hours: number;
  given_hours: number;
  given_past_hours: number;
  given_future_hours: number;
  active_past_hours: number;
  active_future_hours: number;
  done_past_hours: number;
  no_show_past_hours: number;
  intake: { done: number; noshow: number; canceled: number };
  followup: { done: number; noshow: number; canceled: number };
  utilization: number | null;
  caseload: number;
  sustainable_caseload: number;
  load_pct: number | null;
  no_open_hours?: boolean;
  available_hours: number;
  available_slots: number;
  forward_booked: number;
  blocked_hours?: number | null;
  left_hours?: number | null;
  empty_past_hours?: number | null;
  no_load_row?: boolean;
  next_free_slot: string | null;
  accepts_maccabi?: boolean;
  upcoming_count: number;
  upcoming: { date: string; time: string; type: string }[];
}
interface CancelByTypeResp {
  type: 'psy' | 'cm';
  total_hours: number;
  rebooked_hours: number;
  lost_hours: number;
  lost_past_hours: number;
  lost_future_hours: number;
}
interface UnfilledResp { type: string; canceled: number; not_refilled: number }
interface StaffCapacityResp {
  month: string;
  updated_at: string | null;
  given_ready: boolean;
  therapists: TherapistLoadResp[];
  // Opened hours, all of them blocked, nothing happening — outside the roster and outside
  // every total, listed on its own so the dead hours stay visible.
  inactive?: TherapistLoadResp[];
  cancellations?: CancelByTypeResp[];
  unfilled?: UnfilledResp[];
  worked?: Record<string, number>;
}

// Map the real backend rows into the TherapistRow shape the UI renders.
// Fields the view does NOT yet provide (future meetings, mix, empty slots)
// are zero/empty — surfaced honestly via the banner.
function mapStaff(data: StaffCapacityResp, list?: TherapistLoadResp[]): TherapistRow[] {
  return (list ?? data.therapists).map((t) => ({
    id: String(t.therapist_id),
    name: t.name,
    type: t.type === 'psy' ? 'psy' : 'cm',
    hoursGiven: t.given_hours,
    hoursBooked: t.booked_hours,
    doneHours: t.done_hours,
    blockedHours: t.blocked_hours,
    leftHours: t.left_hours,
    emptyPastHours: t.empty_past_hours,
    activeHours: t.active_hours,
    noShowHours: t.no_show_hours,
    givenPastHours: t.given_past_hours,
    givenFutureHours: t.given_future_hours,
    activePastHours: t.active_past_hours,
    activeFutureHours: t.active_future_hours,
    donePastHours: t.done_past_hours,
    noShowPastHours: t.no_show_past_hours,
    intake: t.intake,
    followup: t.followup,
    noOpenHours: t.no_open_hours ?? false,
    availableHours: t.available_hours,
    nextFreeSlot: t.next_free_slot,
    acceptsMaccabi: t.accepts_maccabi ?? true,
    caseload: t.caseload ?? 0,
    sustainableCaseload: t.sustainable_caseload ?? 0,
    loadPct: t.load_pct ?? null,
    hoursTarget: t.type === 'psy' ? CAPACITY_TARGETS.hoursPerPsy : CAPACITY_TARGETS.hoursPerCm,
    futureMeetingCount: t.upcoming_count ?? 0,
    futureMix: { intakeAdult: 0, intakeYouth: 0, fuAdult: 0, fuYouth: 0 },
    emptySlotsCount: t.available_slots ?? 0,
    forwardBooked: t.forward_booked ?? 0,
    noLoadRow: t.no_load_row ?? false,
    upcomingMeetings: t.upcoming ?? [],
    emptySlots: [],
  }));
}

const fmtH1 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n: number) => `${Math.round(n * 100)}%`;
const fmtH   = (n: number) => `${n.toFixed(2)}h`;
// "2026-07-11 11:30:00" / ISO → "11/07 11:30"
const fmtSlot = (iso: string) => {
  const s = iso.replace('T', ' ');
  const [d, t] = s.split(' ');
  const [y, m, dd] = (d || '').split('-');
  return y && m && dd ? `${dd}/${m}${t ? ' ' + t.slice(0, 5) : ''}` : iso;
};

function utilStatus(util: number, target: number): 'good' | 'warn' | 'bad' | 'over' {
  // Over 100% is not "better than good" — it means meetings are sitting on hours
  // nobody opened, so it gets its own reading rather than the green of a full month.
  if (util > 1) return 'over';
  if (util >= target) return 'good';
  if (util >= target * 0.85) return 'warn';
  return 'bad';
}



function Pagination({ page, totalPages, onPage }: { page: number; totalPages: number; onPage: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="cap-pagination">
      <button
        className="cap-page-btn"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >→</button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          className={`cap-page-btn ${p === page ? 'active' : ''}`}
          onClick={() => onPage(p)}
        >{p}</button>
      ))}
      <button
        className="cap-page-btn"
        disabled={page === totalPages}
        onClick={() => onPage(page + 1)}
      >←</button>
    </div>
  );
}

// Capacity = OFFERED (booked + available). Blocked is invisible (yoman-only), so
// we frame everything around what was actually offered for booking — not `given`,
// which includes blocked and is WIP/unreliable.
type CapBreakdown = { offered: number; booked: number; available: number; availableMaccabi: number; availableRest: number; given: number };
function capacityBreakdown(rows: TherapistRow[]): CapBreakdown {
  const sum = (k: keyof TherapistRow) => rows.reduce((a, r) => a + ((r[k] as number) ?? 0), 0);
  const given = sum('hoursGiven');
  const booked = sum('doneHours') + sum('activeHours') + sum('noShowHours');
  // FREE = the REAL bookable field (availableHours), NOT given−booked — `given` includes
  // blocked/non-bookable time, so given−booked overstates "free". Blocked (given−booked−available)
  // is folded into "used" for utilisation, not counted as free. (Ohad 2026-06-28 / 2026-07-09.)
  const available = sum('availableHours');
  // Split the free hours by Maccabi certification: a Clalit-only therapist's free hours can't
  // absorb Maccabi demand (~85% of the queue), so surface "פנוי למכבי" vs "פנוי לכל השאר".
  const availableRest = rows.reduce((a, r) => a + (r.acceptsMaccabi === false ? (r.availableHours ?? 0) : 0), 0);
  const availableMaccabi = available - availableRest;
  return { offered: given, booked, available, availableMaccabi, availableRest, given };
}

// Clean capacity bar — used (booked) vs free (available), over OFFERED capacity.
function CapacityBar({ b }: { b: CapBreakdown }) {
  const seg = (v: number) => (b.offered > 0 ? (v / b.offered) * 100 : 0);
  // "מנוצל" = booked, matching the cards and the table (Ohad, 2026-09-28). It used to
  // be given − available, which filled the bar with every hour that was merely not
  // bookable — so a month whose slots had all expired drew as a fully worked one.
  // Free split by Maccabi certification (matches the "פנוי לזימון" hero card): Maccabi = sky,
  // everyone else = purple (#8B5CF6) — a Clalit-only therapist's free hours can't absorb Maccabi demand.
  // The three segments deliberately no longer fill the bar: what is left is blocked hours
  // plus hours the availability field never measured, and the warehouse cannot tell those
  // apart. The gap is the honest shape of what we do not know.
  // The sky of the middle segment has no token — the palette carries blue, green,
  // red, orange and the five chart hues, and none of them is this cyan. Left as a
  // literal rather than bent into T.blue, which the first segment already owns.
  const parts = [
    { key: 'used', label: 'מוזמן', v: b.booked, color: T.blue },
    { key: 'freeMaccabi', label: 'פנוי · מכבי', v: b.availableMaccabi, color: '#0284C7' },
    { key: 'freeRest', label: 'פנוי · כל השאר', v: b.availableRest, color: SERIES[2] },
  ];
  return (
    <div className="cap-breakdown">
      <div className="cap-breakdown-title">ניצול הקיבולת · {Math.round(b.offered)}h נפתחו ביומן</div>
      <div className="cap-breakdown-bar">
        {parts.map((p) => {
          const pct = seg(p.v);
          if (pct <= 0) return null;
          return (
            <div
              key={p.key}
              className="cap-breakdown-seg"
              style={{ width: `${pct}%`, background: p.color }}
              title={`${p.label}: ${Math.round(p.v)}h (${Math.round(pct)}%)`}
            >
              {pct >= 5 && <span className="cap-breakdown-seg-val">{Math.round(pct)}%</span>}
            </div>
          );
        })}
      </div>
      <div className="cap-breakdown-legend">
        {parts.filter((p) => p.v > 0).map((p) => (
          <span key={p.key} className="cap-breakdown-legend-item">
            <span className="cap-breakdown-dot" style={{ background: p.color }} />
            {p.label} <b>{Math.round(p.v)}h</b> · {Math.round(seg(p.v))}%
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ExecStaff() {
  const navigate = useNavigate();
  const [type, setType] = useState<TherapistTypeFilter>('all');
  const [month, setMonth] = useState<string>(CUR_MONTH);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('util');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [therapistPage, setTherapistPage] = useState(1);

  // Deep-link a therapist's meetings into the unified meetings explorer (per-meeting
  // drilling lives there now, not in a separate cancellations/list page).
  const openExplorer = (name: string, opts: { status?: string; type?: string } = {}) => {
    // Span recent past + the full upcoming pipeline (−3..+6 months) so a future-heavy
    // doctor's SCHEDULED meetings are visible — passing only the current month showed
    // nothing for doctors booked months ahead.
    const now = new Date();
    const months: string[] = [];
    for (let i = -3; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    const p = new URLSearchParams({ therapist: name, months: months.join(',') });
    if (opts.status) p.set('status', opts.status);
    if (opts.type) p.set('type', opts.type);
    navigate(`/exec/meetings?${p.toString()}`);
  };

  // Real staff data from the backend (replaces the old mock `therapists`).
  const [rows, setRows] = useState<TherapistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [worked, setWorked] = useState<Record<string, number>>({});
  // Therapists whose opened hours are 100% blocked — off the roster, listed on their own.
  const [inactiveRows, setInactiveRows] = useState<TherapistRow[]>([]);
  //: When the nightly snapshot that feeds this screen last ran.
  //: 🔴 And a second caveat that matters more than the hour: the nightly job asks the
  //: Yoman about ONE service — a 5-minute "availability probe", not the clinical
  //: services patients actually book. Measured 2026-10-12 in DWH.Request_log: 55 calls
  //: that night, all for service Xztk1YBs, one per therapist. So "free" here means
  //: "free in the probe", and a therapist who is not attached to the probe reads as
  //: zero even with 154 real bookable slots — which is exactly what happened to
  //: Dr Vered Levy. 26 therapists read zero today; 19 of them delivered meetings this
  //: month. The number is not wrong, it answers a narrower question than it looks.
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    execApi.getStaff(month)
      .then((res) => {
        if (cancelled) return;
        const data = res.data as StaffCapacityResp;
        setRows(mapStaff(data));
        setInactiveRows(data.inactive?.length ? mapStaff(data, data.inactive) : []);
        setWorked(data.worked ?? {});
        setSnapshotAt(data.updated_at ?? null);
      })
      .catch(() => {
        if (!cancelled) setError('שגיאה בטעינת נתוני כוח אדם');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [month]);

  // Hero numbers — always both types, independent of table filters (Ohad 2026-06-08)
  const psySummary = useMemo(
    () => getCapacitySummary(rows.filter((t) => t.type === 'psy')),
    [rows],
  );
  const cmSummary = useMemo(
    () => getCapacitySummary(rows.filter((t) => t.type === 'cm')),
    [rows],
  );

  // Capacity breakdown per type — drives the clean "used vs free" bar.
  const psyBreakdown = useMemo(() => capacityBreakdown(rows.filter((t) => t.type === 'psy')), [rows]);
  const cmBreakdown = useMemo(() => capacityBreakdown(rows.filter((t) => t.type === 'cm')), [rows]);
  // Hero utilisation — same definition as the table: (given − available) / given, so blocked
  // counts as used and hero/table always agree. (Ohad 2026-07-09)
  // One definition of ניצולת on this screen, in the cards and in the table alike
  // (Ohad, 2026-09-28): what was DELIVERED ÷ what was opened.
  //
  // It used to be (נפתח − פנוי) ÷ נפתח, which calls every hour that is not bookable
  // "used" — blocked hours and, worse, hours the availability field simply never
  // measured. In a month that has ended almost nothing is still bookable, so the
  // number climbed towards 100% by arithmetic rather than by work: September read
  // 99.66% for case managers and 93.79% for psychiatrists, while the hours actually
  // delivered were 58.01% and 61.44%. A 42-point gap on the card the management reads.
  const heroUtil = (b: CapBreakdown) => (b.given > 0 ? b.booked / b.given : 0);
  const psyUtil = heroUtil(psyBreakdown);
  const cmUtil = heroUtil(cmBreakdown);

  // Table-level filtering — type + search only affect the per-therapist table
  const filtered = useMemo(() => {
    let r = rows;
    if (type !== 'all') r = r.filter((t) => t.type === type);
    if (search) r = r.filter((t) => t.name === search);
    return r;
  }, [rows, type, search]);

  // Name dropdown options, filtered by current type filter
  const nameOptions = useMemo(() => {
    let opts = rows;
    if (type !== 'all') opts = opts.filter((t) => t.type === type);
    return opts.map((t) => t.name);
  }, [rows, type]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const key = sortKey;
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: number | string = 0;
      let bv: number | string = 0;
      switch (key) {
        case 'name':   av = a.name; bv = b.name; break;
        case 'given':  av = a.hoursGiven; bv = b.hoursGiven; break;
        case 'booked': av = a.hoursBooked; bv = b.hoursBooked; break;
        case 'done':   av = a.doneHours ?? 0;   bv = b.doneHours ?? 0;   break;
        case 'active': av = a.activeHours ?? 0; bv = b.activeHours ?? 0; break;
        case 'noshow': av = a.noShowHours ?? 0; bv = b.noShowHours ?? 0; break;
        case 'blocked': av = a.blockedHours ?? -1; bv = b.blockedHours ?? -1; break;
        // -1 sinks the UNMEASURED rows below every real 0.00, so sorting by what
        // is left never puts "we did not look" at the top of "has time free".
        case 'left':   av = a.leftHours ?? -1;    bv = b.leftHours ?? -1;    break;
        case 'emptypast': av = a.emptyPastHours ?? -1; bv = b.emptyPastHours ?? -1; break;
        case 'free':       av = rowFree(a);            bv = rowFree(b);            break;
        case 'util':   av = rowUtil(a); bv = rowUtil(b); break;
        case 'future': av = a.futureMeetingCount; bv = b.futureMeetingCount; break;
        case 'empty':  av = a.emptySlotsCount; bv = b.emptySlotsCount; break;
        case 'fwd':    av = a.forwardBooked ?? 0; bv = b.forwardBooked ?? 0; break;
        case 'caseload': av = a.caseload ?? 0; bv = b.caseload ?? 0; break;
      }
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
      return ((av as number) - (bv as number)) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const selected = selectedId ? rows.find((t) => t.id === selectedId) ?? null : null;

  const setSort = (k: SortKey) => {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
    setTherapistPage(1);
  };

  // Paginate therapists
  const therapistTotalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
  const therapistPageClamped = Math.min(therapistPage, therapistTotalPages);
  const therapistPageRows = sorted.slice(
    (therapistPageClamped - 1) * ROWS_PER_PAGE,
    therapistPageClamped * ROWS_PER_PAGE,
  );

  // Per-type derived values for hero rows. The TARGET is the opened `given`
  // itself (Rana opens fixed forward hours) — no arbitrary 40/60. So we only
  // Real bookable free (the available field) is shown in its OWN hero card ("פנוי לזימון"),
  // so it's NOT repeated in the utilization card's subtitle.

  return (
    <div className="stf-wrap">
      <div className="stf-header">
        <div className="stf-title-block">
          <button className="stf-back-btn" onClick={() => navigate('/exec')}>← חזרה</button>
          <h1 className="stf-title">כוח אדם · שעות וניצולת</h1>
        </div>
        {/* "תחזית עומסים קדימה" button REMOVED per Ohad 2026-07-13 (forecast disconnected here) */}
      </div>

      {/* Global filter — month (affects everything below) */}
      <div className="stf-filters">
        <div className="stf-filter">
          <label className="stf-filter-label">חודש</label>
          <select
            className="stf-filter-select"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          >
            {MONTHS.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
          </select>
        </div>
      </div>

      {snapshotAt && (() => {
        const d = new Date(snapshotAt.replace(' ', 'T'));
        const hrs = (Date.now() - d.getTime()) / 36e5;
        const stale = hrs > 30;        // a nightly job that skipped a night
        return (
          <div className="stf-banner" style={stale ? { color: T.orange } : undefined}>
            {stale ? '⚠ ' : ''}הזמינות נכונה לסנפשוט של{' '}
            <b>{d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b>
            {' '}· שינוי ביומן מאז יופיע רק בריצה הלילית הבאה
            {stale ? ' · 🔴 הסנפשוט לא רץ אתמול' : ''}
            <div style={{ marginTop: 4, fontSize: '0.92em' }}>
              🔴 <b>ו"פנוי" כאן נמדד מול שירות בדיקה אחד ביומן, לא מול השירותים
              שהמטופלים מזמינים בפועל.</b> מטפל שאינו מחובר לשירות הבדיקה מוצג
              כאפס גם כשיש לו סלוטים פנויים. אפס כאן אינו ראיה לחוסר זמינות.
            </div>
          </div>
        );
      })()}

      {loading && <div className="stf-banner">טוען נתונים…</div>}
      {error && <div className="stf-banner" style={{ color: T.red }}>{error}</div>}

      {/* Hero — psychiatrists */}
      <div className="cap-hero-section-head">
        <span className="stf-type-tag psy">פסיכ׳</span>
        <span className="cap-hero-section-title">פסיכיאטרים</span>
      </div>
      <div className="stf-hero">
        <div className="stf-hero-card">
          <div className="stf-hero-label">רופאים פעילים</div>
          <div className="stf-hero-value">{psySummary.headcountPsy}</div>
          <div className="stf-hero-sub">פסיכיאטרים שפתחו יומן · {worked.psy ?? 0} ביצעו</div>
        </div>
        <div className="stf-hero-card">
          <div className="stf-hero-label">סך שעות שנפתחו</div>
          <div className="stf-hero-value">{fmtH1(psySummary.totalGivenPsy)}h</div>
          <div className="stf-hero-sub">סלוטים שראנה פתחה ביומן · זה היעד</div>
        </div>
        <div className="stf-hero-card">
          <div className="stf-hero-label">שעות מנוצלות</div>
          <div className="stf-hero-value" title="מנוצל = כל שעה שיש עליה פגישה ביומן — שהתקיימה, שהמטופל לא הגיע אליה, או שעוד תתקיים. שעה חסומה אינה נספרת.">
            {fmtH1(psyBreakdown.booked)}<span style={{ fontSize: 18, color: T.inkFaint, fontWeight: 600 }}>h</span>
          </div>
          <div className="stf-hero-sub">ניצולת = פגישות ÷ נפתח</div>
          <span className={`stf-hero-delta ${utilStatus(psyUtil, CAPACITY_TARGETS.utilTarget)}`}>
            ניצולת {fmtPct(psyUtil)}
          </span>
        </div>
        <div className="stf-hero-card cap-fillable-card">
          <div className="stf-hero-label">💡 פנוי לזימון</div>
          <div className="stf-hero-value">{fmtH1(psyBreakdown.available)}<span style={{ fontSize: 18, color: T.inkFaint, fontWeight: 600 }}>h</span></div>
          <div className="stf-hero-sub">
            <span style={{ color: T.green, fontWeight: 700 }}>מכבי {fmtH1(psyBreakdown.availableMaccabi)}h</span>
            {' · '}
            <span style={{ color: SERIES[2], fontWeight: 700 }}>כל השאר {fmtH1(psyBreakdown.availableRest)}h</span>
          </div>
        </div>
      </div>
      <CapacityBar b={psyBreakdown} />

      {/* Hero — case managers */}
      <div className="cap-hero-section-head">
        <span className="stf-type-tag cm">CM</span>
        <span className="cap-hero-section-title">מנהלי תיק</span>
      </div>
      <div className="stf-hero">
        <div className="stf-hero-card">
          <div className="stf-hero-label">מנהלי תיק פעילים</div>
          <div className="stf-hero-value">{cmSummary.headcountCm}</div>
          <div className="stf-hero-sub">מנהלי תיק שפתחו יומן · {worked.cm ?? 0} ביצעו</div>
        </div>
        <div className="stf-hero-card">
          <div className="stf-hero-label">סך שעות שנפתחו</div>
          <div className="stf-hero-value">{fmtH1(cmSummary.totalGivenCm)}h</div>
          <div className="stf-hero-sub">סלוטים שנפתחו ביומן · זה היעד</div>
        </div>
        <div className="stf-hero-card">
          <div className="stf-hero-label">שעות מנוצלות</div>
          <div className="stf-hero-value" title="מנוצל = כל שעה שיש עליה פגישה ביומן — שהתקיימה, שהמטופל לא הגיע אליה, או שעוד תתקיים. שעה חסומה אינה נספרת.">
            {fmtH1(cmBreakdown.booked)}<span style={{ fontSize: 18, color: T.inkFaint, fontWeight: 600 }}>h</span>
          </div>
          <div className="stf-hero-sub">ניצולת = פגישות ÷ נפתח</div>
          <span className={`stf-hero-delta ${utilStatus(cmUtil, CAPACITY_TARGETS.utilTarget)}`}>
            ניצולת {fmtPct(cmUtil)}
          </span>
        </div>
        <div className="stf-hero-card cap-fillable-card">
          <div className="stf-hero-label">💡 פנוי לזימון</div>
          <div className="stf-hero-value">{fmtH1(cmBreakdown.available)}<span style={{ fontSize: 18, color: T.inkFaint, fontWeight: 600 }}>h</span></div>
          <div className="stf-hero-sub">שעות פנויות שניתן לאייש</div>
        </div>
      </div>
      <CapacityBar b={cmBreakdown} />

      {/* MAIN TABLE — per therapist */}
      <div className="stf-panel">
        <div className="stf-panel-head cap-panel-head-with-filters">
          <div className="cap-panel-title-block">
            <span className="stf-panel-title">פירוט פר מטפל</span>
            <span className="stf-panel-sub">שעות שניתנו · נוצלו · ניצולת (פגישות עתידיות/סלוטים — בקרוב)</span>
          </div>
          <div className="cap-inline-filters">
            <select
              className={`stf-filter-select ${type !== 'all' ? 'has-value' : ''}`}
              value={type}
              onChange={(e) => setType(e.target.value as TherapistTypeFilter)}
              title="סוג מטפל"
            >
              {TYPES.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
            </select>
            <select
              className={`stf-filter-select ${search ? 'has-value' : ''}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              title="שם מטפל"
            >
              <option value="">כל השמות</option>
              {nameOptions.map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
            <span style={{ fontSize: 12, color: T.inkFaint }}>
              <strong style={{ color: T.borderSoft }}>{sorted.length}</strong> מטפלים
            </span>
          </div>
        </div>
        <table className="cap-table">
          <thead>
            <tr>
              <th onClick={() => setSort('name')} className="sortable">
                שם {sortKey === 'name' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th>סוג</th>
              <th onClick={() => setSort('caseload')} className="sortable" title="מטופלים פעילים שהמטפל מחזיק (עתידי + 90 יום)">
                מטופלים {sortKey === 'caseload' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('given')} className="sortable"
                title="שעות שהוצהרו ביומן בחודש כולו. 🔴 זו הצהרה שמערכת הזימונים אינה אוכפת — נמדד 15/10 שפגישות נקבעות גם בימים שלא הוצהרו כלל — ולכן תפוס יכול לעלות עליה.">
                פתח {sortKey === 'given' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('done')} className="sortable"
                title="שעות של פגישות שכבר התקיימו החודש כולו. מגיע מטבלת השעות שנכתבת ב-03:05, ולכן הוא עד הריצה האחרונה ולא עד הרגע. בחודש סגור הוא מתכנס לדקה מול טבלת הפגישות.">
                בוצע {sortKey === 'done' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('booked')} className="sortable"
                title="כל מה שתפוס בחודש כולו: בוצעו + נקבעו + אי-הגעה. מטבלת השעות של 03:05. 🔴 פגישות שבוטלו אינן נספרות כאן — מטפל שכל חודשו בוטל ייראה כמי שאיש לא הזמין אצלו.">
                תפוס {sortKey === 'booked' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('blocked')} className="sortable"
                title="חסימות יומן שנופלות על שעות שהוצהרו, בחודש כולו. 🔴 רק ההצטלבות נספרת: 88% מהחסימות יושבות על ימים שלא נפתחו כלל, ושם אין מה לגרוע. חיסור כל החסימות שוגה פי 4.9.">
                חסום {sortKey === 'blocked' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('left')} className="sortable"
                title="כמה באמת נשאר להזמין, מהיום עד סוף החודש: מה שהוצהר, פחות האיחוד של הפגישות והחסימות שנופלות בתוכו. שעה שגם חסומה וגם תפוסה נספרת פעם אחת. ריק = טרם נמדד, וזה אינו 0.">
                נשאר <span style={{ fontSize: 10, opacity: 0.6 }}>(מהיום)</span> {sortKey === 'left' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('emptypast')} className="sortable"
                title="שעות שהמטפל פתח ביומן בחלק של החודש שכבר עבר, לא נחסמו, ואיש לא הוזמן אליהן. הן אבדו — אי אפשר להחזיר אותן. 🔴 נמדד 01–14/10: 150.95 שעות על פני הצוות, 15.40% מכל מה שנפתח. לסירין חליליה לבדה 20.00 שעות בשמונה ימים בלי פגישה אחת.">
                עבר ריק <span style={{ fontSize: 10, opacity: 0.6 }}>(עד היום)</span> {sortKey === 'emptypast' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => setSort('util')} className="sortable"
                title="תפוס ÷ פתח — כמה מהשעות שהוצהרו כבר תפוסות. 🔴 זו תפוסה ולא מסירה: פגישה שנקבעה ועוד לא קרתה נספרת בה בדיוק כמו פגישה שבוצעה. ואין קיטום ב-100%: מי שמוזמן יותר ממה שהצהיר יראה מעל 100, וזה ממצא ולא תקלה.">
                ניצולת {sortKey === 'util' && (sortDir === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {therapistPageRows.map((t) => {
              const blocked = t.blockedHours ?? null;   // null = not measured, and that is not 0
              const left = t.leftHours ?? null;
              const emptyPast = t.emptyPastHours ?? null;
              const noMaccabi = t.type === 'psy' && t.acceptsMaccabi === false;  // Maccabi cert is a PSY concept only — never flag CMs
              const util = rowUtil(t);  // same formula as the sort (see rowUtil) so order matches the displayed value
              const notMeasured = t.noLoadRow ?? false;
              // "לא פעיל" asserts the therapist is idle. For an unmeasured row that is
              // a guess, not a measurement — so it never takes the on-leave label.
              const onLeave = !notMeasured && t.hoursGiven === 0 && t.hoursBooked === 0;
              return (
                <tr
                  key={t.id}
                  onClick={() => !onLeave && setSelectedId(t.id)}
                  className={`${onLeave ? 'empty' : 'clickable'} ${selectedId === t.id ? 'selected' : ''}`}
                >
                  <td className="cap-name">
                    <span style={{ color: noMaccabi ? SERIES[2] : undefined }}>{t.name}</span>
                    {notMeasured && (
                      <span
                        title="לא נמדדה לו אף שעה החודש — אין שורה בטבלת השעות ואין פגישה אחת — ובכל זאת משויכים אליו מטופלים פתוחים. השעות מוצגות 0 כי זה מה שידוע, לא כי זה מה שקרה."
                        style={{ marginRight: 6, fontSize: 10, fontWeight: 700, color: T.red,
                          border: `1px solid ${T.red}`, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}
                      >
                        לא נמדד
                      </span>
                    )}
                    {t.noOpenHours && (
                      <span
                        title="אין שעות קבלה מוגדרות ביומן — המטופלים שובצו ידנית. כך סוגרים רופא לזרימה האוטומטית כשרוצים שיקבל רק מטופלים מסוימים."
                        style={{ marginRight: 6, fontSize: 10, fontWeight: 700, color: T.orange,
                          border: `1px solid ${T.orangeInk}`, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}
                      >
                        שיבוץ ידני
                      </span>
                    )}
                    {noMaccabi && (
                      <span
                        title="לא מקבל מכבי — 0 אינטייק מכבי ב-6 החודשים האחרונים; פנוי לכללית בלבד"
                        style={{ marginRight: 6, fontSize: 10, fontWeight: 700, color: SERIES[2],
                          border: `1px solid ${SERIES[2]}`, borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}
                      >
                        כללית בלבד
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`stf-type-tag ${t.type}`}>
                      {t.type === 'psy' ? 'פסיכ׳' : 'CM'}
                    </span>
                  </td>
                  <td>{onLeave ? '—' : <strong style={{ color: '#2563EB' }}>{(t.caseload ?? 0).toLocaleString()}</strong>}</td>
                  <td>
                    {onLeave ? <span style={{ color: T.inkFaint, fontStyle: 'italic' }}>לא פעיל</span> : (
                      <strong>{fmtH(t.hoursGiven)}</strong>
                    )}
                  </td>
                  <td>{onLeave ? '—' : fmtH(t.doneHours ?? 0)}</td>
                  <td>{onLeave ? '—' : (
                    <strong
                      title={t.hoursBooked > t.hoursGiven
                        ? `תפוס ${fmtH(t.hoursBooked)} מול ${fmtH(t.hoursGiven)} שהוצהרו — פגישות נקבעו מחוץ לשעות שהוצהרו`
                        : undefined}
                      style={{ color: t.hoursBooked > t.hoursGiven ? T.orange : undefined }}
                    >
                      {fmtH(t.hoursBooked)}
                    </strong>
                  )}</td>
                  <td>{onLeave ? '—' : (
                    blocked == null
                      ? <span style={{ color: T.inkSoft }} title="טרם נמדד">·</span>
                      : blocked > 0
                        ? <strong style={{ color: T.red }}>{fmtH(blocked)}</strong>
                        : <span style={{ color: T.inkFaint }}>{fmtH(0)}</span>
                  )}</td>
                  <td>{onLeave ? '—' : (
                    left == null
                      ? <span style={{ color: T.inkSoft }}
                          title="טרם נמדד מול היומן — וזה אינו 0. ריק אומר שלא הסתכלנו, לא שאין זמן.">·</span>
                      : <strong
                          title={noMaccabi && left > 0 ? 'פנוי לכללית בלבד — לא מקבל מכבי' : undefined}
                          style={{ color: left > 0 ? (noMaccabi ? SERIES[2] : T.green) : T.inkFaint }}
                        >
                          {fmtH(left)}{noMaccabi && left > 0 ? ' (כ)' : ''}
                        </strong>
                  )}</td>
                  <td>{onLeave ? '—' : (
                    emptyPast == null
                      ? <span style={{ color: T.inkSoft }} title="טרם נמדד">·</span>
                      : emptyPast > 0
                        ? <strong style={{ color: T.orange }}>{fmtH(emptyPast)}</strong>
                        : <span style={{ color: T.inkFaint }}>{fmtH(0)}</span>
                  )}</td>
                  <td>
                    {onLeave ? '—' : (
                      <div className="cap-pct-cell">
                        <div className="cap-pct-track">
                          {/* The bar still stops at the track — it cannot draw past
                              100% — so the NUMBER is what carries the overflow. */}
                          <div
                            className={`cap-pct-fill ${utilStatus(util, CAPACITY_TARGETS.utilTarget)}`}
                            style={{ width: `${Math.min(util, 1) * 100}%` }}
                          />
                        </div>
                        <span style={{ fontWeight: 700, color: util > 1 ? T.orange : undefined }}
                          title={util > 1
                            ? `יש ${fmtH(t.hoursBooked - t.hoursGiven)} פגישות מעבר למה שנפתח ביומן`
                            : undefined}>
                          {fmtPct(util)}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={therapistPageClamped}
          totalPages={therapistTotalPages}
          onPage={setTherapistPage}
        />
      </div>

      {/* Fully-blocked therapists: opened hours, every one of them blocked, nothing booked
          and nothing bookable. Out of the roster and out of the totals — but shown here,
          because those hours are sitting in the Yoman and someone should release them. */}
      {inactiveRows.length > 0 && (
        <div className="cap-card" style={{ marginTop: 16 }}>
          <div className="cap-card-head">
            <h3>לא פעילים החודש · שעות חסומות במלואן</h3>
            <span style={{ fontSize: 12, color: T.inkFaint }}>
              <strong style={{ color: T.borderSoft }}>{inactiveRows.length}</strong> מטפלים ·{' '}
              <strong style={{ color: T.borderSoft }}>
                {inactiveRows.reduce((s, t) => s + t.hoursGiven, 0).toFixed(2)}
              </strong>{' '}
              שעות פתוחות ללא פגישה
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.inkFaint, margin: '0 0 10px' }}>
            נפתחו להם שעות ביומן, אך כולן חסומות: אפס פגישות שבוצעו, אפס משובצות ואפס פנויות
            לזימון. הם אינם נספרים במצבת ואינם משפיעים על הניצולת — השעות האלה פתוחות לשווא
            וכדאי לשחרר אותן ביומן.
          </p>
          <table className="cap-table">
            <thead>
              <tr>
                <th>שם</th>
                <th>סוג</th>
                <th>שעות פתוחות</th>
              </tr>
            </thead>
            <tbody>
              {[...inactiveRows]
                .sort((a, b) => b.hoursGiven - a.hoursGiven)
                .map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.type === 'psy' ? 'פסיכיאטר' : 'מנהל תיק'}</td>
                    <td>{t.hoursGiven.toFixed(2)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DRAWER */}
      {selected && (
        <>
          <div className="cap-drawer-overlay" onClick={() => setSelectedId(null)} />
          <div className="cap-drawer open">
            <div className="cap-drawer-head">
              <button className="stf-back-btn" onClick={() => setSelectedId(null)}>✕</button>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h2 className="cap-drawer-title">{selected.name}</h2>
                  <span className={`stf-type-tag ${selected.type}`}>
                    {selected.type === 'psy' ? 'פסיכ׳' : 'CM'}
                  </span>
                </div>
                <div style={{ marginTop: 4, fontSize: 16, fontWeight: 800, color: '#2563EB' }}>
                  מחזיק {(selected.caseload ?? 0).toLocaleString()} מטופלים
                  <span style={{ fontSize: 11, fontWeight: 400, color: T.inkSoft, marginInlineStart: 6 }}>(פעילים · עתידי + 90 יום)</span>
                </div>
                {selected.loadPct != null && (
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: selected.loadPct > 100 ? T.red : T.green }}>
                    עומס: {selected.loadPct}% מהמקסימום
                    <span style={{ fontSize: 11, fontWeight: 400, color: T.inkSoft, marginInlineStart: 6 }}>
                      (מקס׳ ~{(selected.sustainableCaseload ?? 0).toLocaleString()} מטופלים לפי השעות + קצב מעקב)
                    </span>
                  </div>
                )}
                <div className="cap-drawer-sub">
                  {fmtH(selected.hoursGiven)} ניתנו · {fmtH(selected.hoursBooked)} נוצלו · {fmtH(selected.availableHours ?? 0)} פנויים-לזימון
                </div>
              </div>
            </div>

            <div className="cap-drawer-body">
              {(selected.intake || selected.followup) && (
                <div className="cap-drawer-section">
                  <div className="cap-drawer-section-title">אספקה החודש · אינטייק / מעקב <span style={{ fontWeight: 400, color: T.inkSoft, fontSize: 11 }}>(לחץ לסייר הפגישות)</span></div>
                  {([['אינטייק', 'intake', selected.intake], ['מעקב', 'followup', selected.followup]] as const).map(([lbl, kind, s]) => {
                    if (!s) return null;
                    const sched = (s.done + s.noshow + s.canceled) || 1;
                    const pc = (n: number) => Math.round((n / sched) * 100);
                    const tcode = kind === 'intake' ? (selected.type === 'psy' ? 'psyIntake' : 'cmIntake') : (selected.type === 'psy' ? 'psyFu' : 'cmFu');
                    return (
                      <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, color: T.border, flexWrap: 'wrap', gap: 8 }}>
                        <button onClick={() => openExplorer(selected.name, { type: tcode })}
                          style={{ background: 'none', border: 'none', color: T.border, fontWeight: 700, minWidth: 56, cursor: 'pointer', textAlign: 'right', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>{lbl}</button>
                        <span style={{ color: T.inkFaint }}>בוצע <b style={{ color: T.green }}>{s.done}</b> · נושאו <b>{pc(s.noshow)}%</b> · <button onClick={() => openExplorer(selected.name, { type: tcode, status: 'canceled' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.red, font: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>ביטול <b>{pc(s.canceled)}%</b></button></span>
                      </div>
                    );
                  })}
                </div>
              )}

              <button onClick={() => openExplorer(selected.name)}
                style={{ width: '100%', background: T.blue, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', margin: '4px 0 8px' }}>
                כל הפגישות של {selected.name} בסייר ←
              </button>

              <div className="cap-drawer-section">
                <div className="cap-drawer-section-title">
                  פגישות עתידיות
                  <span className="cap-drawer-count">{selected.futureMeetingCount}</span>
                </div>
                {selected.upcomingMeetings.length === 0 ? (
                  <div className="cap-drawer-empty">אין פגישות עתידיות מתוכננות</div>
                ) : (
                  <>
                    <table className="cap-table cap-drawer-table">
                      <thead>
                        <tr><th>תאריך</th><th>שעה</th><th>סוג</th></tr>
                      </thead>
                      <tbody>
                        {selected.upcomingMeetings.map((m, idx) => (
                          <tr key={idx}>
                            <td>{m.date}</td>
                            <td>{m.time}</td>
                            <td>{m.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {selected.futureMeetingCount > selected.upcomingMeetings.length && (
                      <button onClick={() => openExplorer(selected.name, { status: 'active' })}
                        style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 12, marginTop: 6, textDecoration: 'underline' }}>
                        ועוד {selected.futureMeetingCount - selected.upcomingMeetings.length} — ראה הכל בסייר ←
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="cap-drawer-section">
                <div className="cap-drawer-section-title">
                  סלוטים ריקים
                  <span className="cap-drawer-count" style={{ background: T.orange, color: T.sidebar }}>
                    {selected.emptySlotsCount}
                  </span>
                </div>
                <div className="cap-drawer-empty">
                  {selected.emptySlotsCount > 0
                    ? `${selected.emptySlotsCount} סלוטים פנויים לזימון${selected.nextFreeSlot ? ` · הסלוט הבא: ${fmtSlot(selected.nextFreeSlot)}` : ''}`
                    : 'אין סלוטים פנויים זמינים'}
                  <div style={{ fontSize: 11, color: T.inkSoft, marginTop: 4 }}>(רשימת-זמנים מפורטת — מהיומן, טרם מסונכרן)</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
